export interface GastroSalesSandboxOrder {
  id: string;
  customer: string;
  fulfillmentType: string;
  status: string;
  revision: number;
  totalMinor: number;
  createdAt: string;
  updatedAt: string;
  lines: readonly { id: string; name: string; quantity: number; lineTotalMinor: number }[];
}

export interface GastroSalesSandboxProductionWork {
  id: string;
  orderId: string;
  itemName: string;
  quantity: number;
  unit: string;
  status: string;
  revision: number;
  queuedAt: string;
}

export interface GastroSalesSandboxInventoryItem {
  id: string;
  name: string;
  componentKey: string | null;
  amount: number;
  unit: string;
}

export interface GastroSalesSandboxSnapshot {
  schemaVersion: 1;
  runtime: "canonical-backend";
  stateMode: "isolated-sandbox";
  externalActions: "blocked";
  organizationId: string;
  locationId: string;
  menu: { itemId: string; name: string; unitPriceMinor: number };
  orders: readonly GastroSalesSandboxOrder[];
  production: readonly GastroSalesSandboxProductionWork[];
  inventory: readonly GastroSalesSandboxInventoryItem[];
}

interface ApiEnvelope<T> {
  data?: T;
  error?: { code?: string; message?: string };
  requestId?: string;
}

type Fetcher = typeof fetch;

export class GastroSalesSandboxClientError extends Error {
  constructor(readonly status: number, readonly code: string, message: string) {
    super(message);
    this.name = "GastroSalesSandboxClientError";
  }
}

const BASE = "/api/v1/platform/sales-demo/gastro";
const ORDER_ID = /^cd_ord_[A-Za-z0-9._:-]{1,160}$/;
const PRODUCTION_WORK_ID = /^cd_gprod_work_[A-Za-z0-9._:-]{1,160}$/;

export class GastroSalesSandboxClient {
  constructor(private readonly fetcher: Fetcher = globalThis.fetch.bind(globalThis)) {}

  state(): Promise<GastroSalesSandboxSnapshot> {
    return this.request(BASE);
  }

  reset(): Promise<GastroSalesSandboxSnapshot> {
    return this.mutate(`${BASE}/reset`, {});
  }

  createOrder(input: { contactName?: string; quantity?: number } = {}): Promise<GastroSalesSandboxSnapshot> {
    return this.mutate(`${BASE}/orders`, input);
  }

  acceptOrder(orderId: string): Promise<GastroSalesSandboxSnapshot> {
    if (!ORDER_ID.test(orderId)) throw new GastroSalesSandboxClientError(400, "order_id_invalid", "Pedido demo inválido.");
    return this.mutate(`${BASE}/orders/${encodeURIComponent(orderId)}/accept`, {});
  }

  completeOrder(orderId: string): Promise<GastroSalesSandboxSnapshot> {
    if (!ORDER_ID.test(orderId)) throw new GastroSalesSandboxClientError(400, "order_id_invalid", "Pedido demo inválido.");
    return this.mutate(`${BASE}/orders/${encodeURIComponent(orderId)}/complete`, {});
  }

  startProduction(productionWorkId: string): Promise<GastroSalesSandboxSnapshot> {
    if (!PRODUCTION_WORK_ID.test(productionWorkId)) throw new GastroSalesSandboxClientError(400, "production_id_invalid", "Trabajo de cocina demo inválido.");
    return this.mutate(`${BASE}/production/${encodeURIComponent(productionWorkId)}/start`, {});
  }

  readyProduction(productionWorkId: string): Promise<GastroSalesSandboxSnapshot> {
    if (!PRODUCTION_WORK_ID.test(productionWorkId)) throw new GastroSalesSandboxClientError(400, "production_id_invalid", "Trabajo de cocina demo inválido.");
    return this.mutate(`${BASE}/production/${encodeURIComponent(productionWorkId)}/ready`, {});
  }

  private async csrf(): Promise<string> {
    const response = await this.fetcher("/api/v1/platform/auth/csrf", { credentials: "include" });
    const body = await response.json().catch(() => ({})) as ApiEnvelope<{ authenticated: boolean; csrfToken: string }>;
    if (!response.ok || !body.data?.csrfToken) {
      throw new GastroSalesSandboxClientError(response.status, body.error?.code ?? "csrf_required", body.error?.message ?? "No se pudo obtener el token de seguridad.");
    }
    return body.data.csrfToken;
  }

  private async mutate(path: string, body: Readonly<Record<string, unknown>>): Promise<GastroSalesSandboxSnapshot> {
    const csrfToken = await this.csrf();
    return this.request(path, {
      method: "POST",
      credentials: "include",
      headers: {
        "content-type": "application/json",
        "x-csrf-token": csrfToken,
      },
      body: JSON.stringify(body),
    });
  }

  private async request(path: string, init: RequestInit = {}): Promise<GastroSalesSandboxSnapshot> {
    const response = await this.fetcher(path, { credentials: "include", ...init });
    const body = await response.json().catch(() => ({})) as ApiEnvelope<GastroSalesSandboxSnapshot>;
    if (!response.ok || !body.data) {
      throw new GastroSalesSandboxClientError(
        response.status,
        body.error?.code ?? `sales_sandbox_${response.status}`,
        body.error?.message ?? "La sandbox gastronómica no pudo completar la acción.",
      );
    }
    return body.data;
  }
}
